import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, Trash2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['', 'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO',
  'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const VARIABLES = ['{PERIODO}', '{PERIODO_CORTO}', '{PERIODO_MM_AAAA}', '{MES}', '{AÑO}', '{CANTIDAD}',
  '{CONVENIO}', '{CONCEPTO}', '{TIPO_AFILIADO}', '{TIPO_AFILIADO_MAY}', '{RESOLUCION}', '{TOTAL}'];

export const InvoiceFormatDialog = ({ insurer, services, open, onClose, onSaved }) => {
  const [templates, setTemplates] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  const [lineMode, setLineMode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !insurer) return;
    setTemplates((insurer.invoice_templates || []).map(t => ({ ...t })));
    setResolutions((insurer.resolution_history || []).map(r => ({ ...r })));
    setLineMode(insurer.invoice_line_mode || '');
  }, [open, insurer]);

  if (!insurer) return null;

  const affiliateTypes = insurer.affiliate_types || [];
  const insurerServices = (insurer.service_ids || [])
    .map(sid => services.find(s => s.service_id === sid))
    .filter(Boolean);

  const now = new Date();
  const currentResolution = (() => {
    const today = now.toISOString().slice(0, 10);
    const past = resolutions.filter(r => r.valid_from && r.valid_from <= today)
      .sort((a, b) => a.valid_from.localeCompare(b.valid_from));
    return past.length ? past[past.length - 1].resolution : (resolutions[0]?.resolution || '');
  })();

  const preview = (t) => {
    const mes = MONTHS[now.getMonth() + 1];
    const svc = insurerServices.find(s => s.service_id === t.service_id);
    const map = {
      '{PERIODO}': `${mes} ${now.getFullYear()}`,
      '{PERIODO_CORTO}': `${mes}/${now.getFullYear()}`,
      '{PERIODO_MM_AAAA}': `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`,
      '{MES}': mes,
      '{AÑO}': String(now.getFullYear()),
      '{CANTIDAD}': '4',
      '{CONVENIO}': insurer.name || '',
      '{CONCEPTO}': svc?.name || 'Concepto del catálogo',
      '{TIPO_AFILIADO}': t.affiliate_type || '(todos los tipos)',
      '{TIPO_AFILIADO_MAY}': (t.affiliate_type || '(TODOS LOS TIPOS)').toUpperCase(),
      '{RESOLUCION}': currentResolution || '(sin resolución cargada)',
      '{TOTAL}': '6.000.000,00',
    };
    return Object.entries(map).reduce((acc, [k, v]) => acc.split(k).join(v), t.text || '')
      .replace(/\s+/g, ' ').trim();
  };

  const setT = (i, k, v) => setTemplates(prev => prev.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const addTemplate = () => setTemplates(prev => [...prev, {
    name: `Modelo ${prev.length + 1}`, text: '', affiliate_type: '', service_id: '',
  }]);
  const addResolution = () => setResolutions(prev => [...prev, { resolution: '', valid_from: '' }]);

  const save = async () => {
    const bad = templates.find(t => !(t.text || '').trim());
    if (bad) { toast.error('Hay un modelo sin texto: escribilo o borralo'); return; }
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}`, {
        invoice_templates: templates.map(t => ({
          template_id: t.template_id, name: t.name, text: t.text,
          affiliate_type: t.affiliate_type || null, service_id: t.service_id || null,
        })),
        resolution_history: resolutions.filter(r => r.resolution && r.valid_from),
        ...(lineMode ? { invoice_line_mode: lineMode } : {}),
      }, { withCredentials: true });
      toast.success('Formato de factura guardado');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-teal-600" />
            Formato de factura — {insurer.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-xs text-muted-foreground">
            Lo que la obra social quiere leer en el renglón de la factura. Si un concepto no tiene
            modelo asignado, se imprime el nombre que tiene en el catálogo.
          </p>

          <div className="rounded-md border p-3 space-y-2">
            <Label>Resoluciones (para la variable <code>{'{RESOLUCION}'}</code>)</Label>
            {resolutions.length === 0 && (
              <p className="text-xs text-muted-foreground">Sin resoluciones cargadas.</p>
            )}
            {resolutions.map((r, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={r.resolution} placeholder="1008/2026"
                       onChange={(e) => setResolutions(prev => prev.map((x, idx) => idx === i ? { ...x, resolution: e.target.value } : x))}
                       data-testid={`resolution-value-${i}`} />
                <Input type="date" value={r.valid_from || ''}
                       onChange={(e) => setResolutions(prev => prev.map((x, idx) => idx === i ? { ...x, valid_from: e.target.value } : x))}
                       data-testid={`resolution-from-${i}`} />
                <Button variant="ghost" size="icon" className="text-red-600"
                        onClick={() => setResolutions(prev => prev.filter((_, idx) => idx !== i))}
                        data-testid={`resolution-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={addResolution} data-testid="resolution-add">
                <Plus className="w-3 h-3 mr-1" />Agregar resolución
              </Button>
              <span className="text-[11px] text-muted-foreground">
                Vigente hoy: <b>{currentResolution || '—'}</b>
              </span>
            </div>
          </div>

          <div>
            <Label>Cómo se arma el renglón por defecto</Label>
            <select value={lineMode} onChange={(e) => setLineMode(e.target.value)}
                    className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="line-mode">
              <option value="">Automático (resumido si hay modelo de texto)</option>
              <option value="summary">Resumido: un renglón por concepto, cantidad 1</option>
              <option value="detail">Detallado: un renglón por afiliado</option>
            </select>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Modelos de texto ({templates.length})</Label>
              <Button variant="outline" size="sm" onClick={addTemplate} data-testid="template-add">
                <Plus className="w-3 h-3 mr-1" />Nuevo modelo
              </Button>
            </div>
            {templates.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Sin modelos: la factura se imprime como hasta ahora, con el nombre del concepto.
              </p>
            )}
            {templates.map((t, i) => (
              <div key={i} className="rounded-md border p-3 space-y-2" data-testid={`template-${i}`}>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Nombre del modelo</Label>
                    <Input value={t.name || ''} onChange={(e) => setT(i, 'name', e.target.value)}
                           placeholder="IOMA voluntarios individuales con AGD"
                           data-testid={`template-name-${i}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo de afiliado</Label>
                    <select value={t.affiliate_type || ''} onChange={(e) => setT(i, 'affiliate_type', e.target.value)}
                            className="flex h-10 w-full rounded-md border px-2 text-sm"
                            data-testid={`template-type-${i}`}>
                      <option value="">Todos</option>
                      {affiliateTypes.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Concepto</Label>
                    <select value={t.service_id || ''} onChange={(e) => setT(i, 'service_id', e.target.value)}
                            className="flex h-10 w-full rounded-md border px-2 text-sm"
                            data-testid={`template-service-${i}`}>
                      <option value="">Todos</option>
                      {insurerServices.map(s => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Texto del renglón</Label>
                  <Textarea rows={3} value={t.text || ''} onChange={(e) => setT(i, 'text', e.target.value)}
                            placeholder="INTERNACIÓN PERMANENTE EN HOGAR POR MES PRESTACIONAL {PERIODO} POR {CANTIDAD} AFILIADOS {TIPO_AFILIADO_MAY} VALOR SEGÚN RESOLUCIÓN {RESOLUCION} SERVICIOS BRINDADOS SEGÚN SE DETALLA EN RESUMEN MENSUAL"
                            data-testid={`template-text-${i}`} />
                </div>
                <div className="flex flex-wrap gap-1">
                  {VARIABLES.map(v => (
                    <button key={v} type="button"
                            className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-teal-50"
                            onClick={() => setT(i, 'text', `${t.text || ''}${(t.text || '').endsWith(' ') || !t.text ? '' : ' '}${v}`)}
                            data-testid={`template-var-${i}-${v}`}>{v}</button>
                  ))}
                </div>
                <div className="rounded bg-slate-50 border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Así se imprime</div>
                  <div className="text-xs" data-testid={`template-preview-${i}`}>
                    {preview(t) || <span className="text-muted-foreground">—</span>}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <Badge variant="outline" className="text-[10px]">
                    {t.affiliate_type || 'todos los tipos'} · {insurerServices.find(s => s.service_id === t.service_id)?.name || 'todos los conceptos'}
                  </Badge>
                  <Button variant="ghost" size="sm" className="text-red-600"
                          onClick={() => setTemplates(prev => prev.filter((_, idx) => idx !== i))}
                          data-testid={`template-del-${i}`}>
                    <Trash2 className="w-3 h-3 mr-1" />Borrar modelo
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="save-invoice-format">
            {saving ? 'Guardando…' : 'Guardar formato'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
